import {
  List, Datagrid, TextField, BooleanField, EditButton, Edit, Create, SimpleForm,
  TextInput, BooleanInput, NumberInput, required,
  AutocompleteInput, useGetList,
} from 'react-admin';
import { Stack, Box, Typography } from '@mui/material';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { ImageUploadInput } from '../../components/ImageUploadInput.jsx';
import { ReorderableDatagrid, imageCell } from '../../components/ReorderableDatagrid.jsx';

const filters = [
  <TextInput key="q" source="q" label="Search key/label/group" alwaysOn />,
];

const CampusList = () => (
  <List
    sort={{ field: 'sortOrder', order: 'ASC' }}
    perPage={100}
    pagination={false}
    filters={filters}
    empty={false}
  >
    <ReorderableDatagrid
      columns={[
        { key: 'image',      header: 'Image',       width: 110, render: (r) => imageCell(r.image) },
        { key: 'shortLabel', header: 'Campus',                 render: (r) => r.shortLabel || r.key },
        { key: 'groupLabel', header: 'Group',       width: 200, render: (r) => r.groupLabel || '—' },
        { key: 'isActive',   header: 'Active',      width: 80,  render: (r) => r.isActive ? '✓' : '—' },
      ]}
    />
  </List>
);

/**
 * Group input — free-form text but autosuggests from existing groupLabel
 * values so the admin doesn't accidentally create "Dhanmondi Campuses"
 * and "Dhanmondi campuses" as two different groups.
 */
function GroupSelect() {
  const { data } = useGetList(
    'campuses',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'sortOrder', order: 'ASC' } },
  );
  const choices = Array.from(new Set((data || []).map((c) => c.groupLabel).filter(Boolean)))
    .map((g) => ({ id: g, name: g }));
  return (
    <AutocompleteInput
      source="groupLabel"
      choices={choices}
      onCreate={(name) => ({ id: name, name })}
      label="Group"
      helperText='The right-side list groups campuses by this label. E.g. "Main Campus" / "Dhanmondi Campuses" / "Early Years Campuses".'
      fullWidth
    />
  );
}

const CampusForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 760 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput
        source="key"
        label="Key (slug)"
        helperText='Stable handle, e.g. "main", "r11a-95". Lowercase, no spaces.'
        validate={required()}
        sx={{ width: 220 }}
      />
      <NumberInput source="sortOrder" defaultValue={0} sx={{ width: 140 }} />
      <BooleanInput source="isActive" label="Active" defaultValue={true} />
    </Stack>

    <TextInput
      source="shortLabel"
      label="Short label (right-side list)"
      helperText='Used in the schools-list column on desktop. E.g. "House 95/1, Road 11/A".'
      validate={required()}
      fullWidth
    />

    <GroupSelect />

    <TextInput
      source="kicker"
      label="Kicker (eyebrow above title)"
      helperText='Optional. E.g. "Main Campus · Head Office".'
      fullWidth
    />

    <TextInput
      source="rawTitle"
      label="Title"
      validate={required()}
      helperText='Wrap italic words in <em>…</em>. E.g. "House 95/1, <em>Road 11/A</em>".'
      fullWidth
    />

    <ImageUploadInput source="image" label="Campus image" />

    <TextInput
      source="body"
      label="Description"
      multiline
      minRows={3}
      fullWidth
      helperText="Short paragraph shown on the big image side."
    />

    <TextInput
      source="programmes"
      label="Programmes"
      multiline
      minRows={3}
      fullWidth
      helperText="One programme per line. E.g. 'Class KG I — January & July Session'."
    />

    <Box sx={{ background: 'var(--cream, #faf6ec)', border: '1px dashed var(--line, #e6e1d4)', borderRadius: 1, p: 1.5, mt: 1 }}>
      <Typography variant="caption" sx={{ color: 'var(--ink-3, #5d6660)' }}>
        <strong>Layout:</strong> the public homepage shows the campus image (left) and the right-side list groups campuses by the <code>Group</code> field. The first campus by <code>sortOrder</code> is the default image shown when no one is hovering.
      </Typography>
    </Box>
  </Stack>
);

const CampusEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm><CampusForm /></SimpleForm>
  </Edit>
);

const CampusCreate = () => (
  <Create redirect="list">
    <SimpleForm><CampusForm /></SimpleForm>
  </Create>
);

export default {
  list: CampusList,
  edit: CampusEdit,
  create: CampusCreate,
  icon: LocationCityIcon,
  options: { label: 'Campuses' },
  recordRepresentation: 'shortLabel',
};
